'use client'

import React, { useRef, useState } from 'react'

import { Box, Text } from '@chakra-ui/react'
import { keyframes } from '@emotion/react'

import type { ColorScheme } from '../../types'
import { AlertCircleIcon } from '../Icon'
import { Tooltip } from '../Tooltip'
import type { TooltipVariant } from '../Tooltip'
import {
  dotStyles,
  errorIconColor,
  segmentLabelStyles,
  segmentStyles,
  spinnerStyles,
  trackStyles,
} from './SegmentedControl.styles'

export type SegmentedControlVariant = 'default' | 'accent'

export type SegmentedControlOptionState =
  | 'ready'
  | 'loading'
  | 'disabled'
  | 'error'

export type SegmentedControlOption = {
  value: string
  label: string
  icon?: React.ReactNode
  state?: SegmentedControlOptionState
  hasDot?: boolean
  /** Shown on hover. Works on non-clickable segments too (loading, disabled, error). */
  tooltip?: React.ReactNode
  /** 'label' (default) is a short uppercase hint; 'description' wraps longer explanatory copy. */
  tooltipVariant?: TooltipVariant
}

export type SegmentedControlProps = {
  options: SegmentedControlOption[]
  value: string
  onChange: (value: string) => void
  colorScheme?: ColorScheme
  height?: string
  width?: string
  variant?: SegmentedControlVariant
}

const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`

const Spinner = ({ colorScheme }: { colorScheme: ColorScheme }) => (
  <Box
    animation={`${spin} 0.8s linear infinite`}
    aria-label='Loading'
    data-testid='segmented-control-spinner'
    role='status'
    {...spinnerStyles(colorScheme)}
  />
)

/** Matches the spinner's 12x12 box so loading → error causes no layout shift. */
const ERROR_ICON_SIZE = 12

type SegmentProps = {
  option: SegmentedControlOption
  isSelected: boolean
  onChange: (value: string) => void
  colorScheme: ColorScheme
  variant: SegmentedControlVariant
}

const Segment = ({
  option,
  isSelected,
  onChange,
  colorScheme,
  variant,
}: SegmentProps) => {
  const [isHovering, setIsHovering] = useState(false)
  const segmentRef = useRef<HTMLDivElement>(null)

  const state = option.state ?? 'ready'
  const isLoading = state === 'loading'
  const isDisabled = state === 'disabled'
  const isError = state === 'error'
  // An errored view can never be the displayed one, so it never renders as active — otherwise the
  // active treatment would override the muted error styling and suppress its dot.
  const isActive = isSelected && !isError
  const isClickable = !isActive && !isLoading && !isDisabled && !isError
  const hasDot = option.hasDot && !isActive && (state === 'ready' || isError)
  const shouldShowTooltip =
    option.tooltip != null && isHovering && segmentRef.current !== null

  return (
    <Box
      aria-busy={isLoading || undefined}
      aria-disabled={isDisabled || isLoading || isError || undefined}
      aria-pressed={isActive}
      onClick={isClickable ? () => onChange(option.value) : undefined}
      onKeyDown={(e) => {
        if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onChange(option.value)
        }
      }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      ref={segmentRef}
      role='button'
      tabIndex={isActive || isClickable ? 0 : -1}
      {...segmentStyles(
        isActive,
        colorScheme,
        state,
        option.tooltip != null,
        variant
      )}
    >
      {/* The alert glyph, the spinner and the option's own icon all share one leading slot. */}
      {isError && (
        <AlertCircleIcon
          color={errorIconColor(colorScheme)}
          size={ERROR_ICON_SIZE}
        />
      )}
      {!isError && isLoading && <Spinner colorScheme={colorScheme} />}
      {!isError && !isLoading && option.icon}
      <Text {...segmentLabelStyles}>{option.label}</Text>
      {hasDot && (
        <Box
          data-testid='segmented-control-dot'
          {...dotStyles(colorScheme, isError ? 'error' : 'notification')}
        />
      )}
      {shouldShowTooltip && (
        <Tooltip
          anchorEl={segmentRef.current}
          colorScheme={colorScheme}
          label={option.tooltip}
          placement='bottom'
          variant={option.tooltipVariant}
        />
      )}
    </Box>
  )
}

export const SegmentedControl = ({
  options,
  value,
  onChange,
  colorScheme = 'dark',
  height = '100%',
  width,
  variant = 'default',
}: SegmentedControlProps) => (
  <Box {...trackStyles(colorScheme, variant)} height={height} width={width}>
    {options.map((option) => (
      <Segment
        colorScheme={colorScheme}
        isSelected={option.value === value}
        key={option.value}
        onChange={onChange}
        option={option}
        variant={variant}
      />
    ))}
  </Box>
)
